import { Component, OnInit, Input, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { Table, Pagination, TableData, TableDataTypes } from 'src/app/shared/types';
import { CaptainRedeemService } from 'src/app/captain-redeem-service';
import * as moment from 'moment';

@Component({
  selector: 'app-host-wallet-transactions',
  templateUrl: './host-wallet-transactions.component.html',
  styleUrls: ['./host-wallet-transactions.component.css']
})
export class HostWalletTransactionsComponent implements OnInit, OnChanges {
  @Input() profileId: string;
  @Input() hostDetailsToggleableHeaders: string[];
  @Output() toggleHeaders: EventEmitter<any> = new EventEmitter();
  transactionsTable: Table;
  transactions: any[] = [];
  perPage: number = 10;
  next: string = "";
  prev: string = "";
  firstElement: string = "";
  lastElement: string = "";
  dataLoaded: boolean = false;

  constructor(
    private redeemService: CaptainRedeemService
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.profileId && changes.profileId.currentValue) {
      this.profileId = changes.profileId.currentValue;
      if (this.profileId) {
        this.initializeTable();
        this.fillDataToTable();
      }
    }
  }

  ngOnInit() {
    if (this.profileId) {
      this.initializeTable();
      this.fillDataToTable();
    }
  }

  changeInHeader(header) {
    this.toggleHeaders.emit(header);
  }

  initializeTable(pageNumber = 0) {
    this.transactionsTable = new Table({
      toggleableHeaders: this.hostDetailsToggleableHeaders,
      selectedHeader: this.hostDetailsToggleableHeaders.indexOf('Wallet Transactions'),
      headers: {
        dateTime: 'Date & Time',
        type: 'Type',
        amount: 'Amount',
        transactionType: 'Transaction Type',
        transactionId: 'Transaction ID',
        status: 'Status'
      },
      pagination: new Pagination(pageNumber, this.perPage),
      defaultError: 'No transactions found',
      data: [],
      config: {
        refresh: true,
      },
      onRefresh: () => {
        this.transactionsTable = null;
        this.next = "";
        this.prev = "";
        this.ngOnInit();
      }
    });
  }

  onPaginationChange(event: { perPage: number, previousPageIndex: number, pageNo: number }) {
    if (event.pageNo < event.previousPageIndex) {
      this.next = "";
      this.prev = this.firstElement;
    } else if (event.pageNo > event.previousPageIndex) {
      this.prev = "";
      this.next = this.lastElement;
    }
    this.perPage = event.perPage;
    this.fillDataToTable();
  }

  async fillDataToTable() {
    if (this.profileId) {
      this.dataLoaded = false;
      const requestBody = {
        "transactionType": ["orders", "redeem", "referrals"]
      };

      try {
        const response: any = await this.redeemService.getWalletCaptainPassbookFromWallet(
          this.profileId,
          requestBody,
          this.perPage,
          this.next,
          this.prev
        );

        this.dataLoaded = true;
        const transactionArray = response && response.data ? response.data : [];

        if (transactionArray.length > 0) {
          this.firstElement = transactionArray[0].id;
          this.lastElement = transactionArray[transactionArray.length - 1].id;
        }

        const createTableData = (value, className?) => {
          return new TableData({
            data: value,
            type: TableDataTypes.DATA,
            className: className ? className + ' cursor-pointer' : 'cursor-pointer whiteSpacePre'
          });
        };

        this.transactions = transactionArray.map((transaction) => {
          const formattedDate = this.formatDateToIST(transaction.time);
          const formattedAmount = this.formatAmount(transaction.amount, transaction.type);
          const transactionTypeDisplay = this.mapTransactionType(transaction.transactionType);
          const statusDisplay = this.mapStatus(transaction.status);
          const typeDisplay = transaction.type ? transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1) : 'N/A';
          const transactionId = transaction.id || 'N/A';

          return {
            dateTime: createTableData(formattedDate),
            type: createTableData(typeDisplay),
            amount: createTableData(formattedAmount, transaction.type === 'debit' ? 'color-red' : 'color-green'),
            transactionType: createTableData(transactionTypeDisplay),
            transactionId: createTableData(transactionId),
            status: createTableData(statusDisplay)
          };
        });

        this.transactionsTable.data = this.transactions;
        if (transactionArray.length === this.perPage) {
          this.transactionsTable.pagination.count = (this.transactionsTable.pagination.pageNo + 1) * this.perPage + 1;
        } else {
          this.transactionsTable.pagination.count = (this.transactionsTable.pagination.pageNo * this.perPage) + transactionArray.length;
        }
      } catch (err) {
        this.dataLoaded = true;
        this.transactionsTable.data = [];
        this.transactionsTable.pagination.count = 0;
      }
    } else {
      this.dataLoaded = true;
    }
  }

  mapTransactionType(transactionType: string): string {
    const typeMap: { [key: string]: string } = {
      'orders': 'Orders',
      'redeem': 'Redeem',
      'referrals': 'Referrals'
    };
    return typeMap[transactionType] || (transactionType ? transactionType.charAt(0).toUpperCase() + transactionType.slice(1) : 'N/A');
  }

  mapStatus(status: string): string {
    const statusMap: { [key: string]: string } = {
      'success': 'Completed',
      'pending': 'Pending',
      'failed': 'Failed',
      'cancelled': 'Cancelled'
    };
    return statusMap[status] || (status ? status.charAt(0).toUpperCase() + status.slice(1) : 'N/A');
  }

  formatDateToIST(time: number): string {
    if (!time) {
      return 'N/A';
    }
    // Convert epoch timestamp (milliseconds) to IST
    const istDate = moment(time).utcOffset('+05:30');
    const dateInfo = istDate.format('DD MMM YYYY');
    const timeInfo = istDate.format('HH:mm:ss');
    return `${dateInfo}\n${timeInfo}`;
  }

  formatAmount(amount: number, type: string): string {
    if (amount === null || amount === undefined) {
      return 'N/A';
    }
    const formattedAmount = Math.abs(amount).toFixed(2);
    const sign = type === 'debit' ? '-' : '';
    return `${sign}₹${formattedAmount}`;
  }
}

